import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ENABLED_LANGUAGES from '@alltrails/core/constants/enabledLanguages';
import SUPPORTED_LANGUAGES from '@alltrails/core/constants/supportedLanguages';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import useLanguageChangeHandler from './useLanguageChangeHandler';

type Args = {
  enabledLanguages?: string[];
  languageRegionCode: LanguageRegionCode;
  pageReloadOnLanguageChange?: boolean;
};

// Selection state and options shared by the language pickers, so each one only owns its presentation.
export default function useLanguageSelect({ enabledLanguages = ENABLED_LANGUAGES, languageRegionCode, pageReloadOnLanguageChange }: Args) {
  const intl = useIntl();
  const [selectedLanguage, setSelectedLanguage] = useState(languageRegionCode);
  const onLanguageChange = useLanguageChangeHandler(pageReloadOnLanguageChange);

  const languages = useMemo(() => SUPPORTED_LANGUAGES.filter(l => enabledLanguages.includes(l.languageRegionCode)), [enabledLanguages]);

  const selectLanguage = useCallback(
    (newLanguageRegionCode: LanguageRegionCode) => {
      if (selectedLanguage === newLanguageRegionCode) {
        return;
      }

      setSelectedLanguage(newLanguageRegionCode);
      onLanguageChange(newLanguageRegionCode);
    },
    [onLanguageChange, selectedLanguage]
  );

  return {
    label: intl.formatMessage({ defaultMessage: 'Select a language' }),
    languages,
    selectedLanguage,
    selectLanguage
  };
}
